import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';
import { TrendUp, TrendDown } from '@phosphor-icons/react';
import api from '../lib/api';
import { useCurrency } from '../context/CurrencyContext';

const RANGES = [
  { code: '1D', label: '1D' },
  { code: '1W', label: '1W' },
  { code: '1M', label: '1M' },
  { code: '1Y', label: '1Y' },
];

const formatTooltipDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

const PortfolioMiniChart = () => {
  const { format } = useCurrency();
  const [range, setRange] = useState('1W');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (r) => {
    setLoading(true);
    try {
      const res = await api.get(`/portfolio/history?range=${r}`);
      setData(res.data);
    } catch {
      setData({ points: [], change_pct: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(range); }, [range, load]);

  const change = data?.change_pct ?? 0;
  const isPositive = change >= 0;
  const stroke = isPositive ? '#34d399' : '#f87171';
  const fillId = isPositive ? 'gradPos' : 'gradNeg';

  return (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-900/60 to-zinc-950/80 p-4" data-testid="portfolio-mini-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Portfolio Performance</span>
          <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${isPositive ? 'text-emerald-300' : 'text-pink-300'}`}>
            {isPositive ? <TrendUp size={12} weight="fill" /> : <TrendDown size={12} weight="fill" />}
            {isPositive ? '+' : ''}{change.toFixed(2)}%
          </span>
        </div>
        <div className="flex gap-1 p-0.5 rounded-full bg-black/40 border border-white/5">
          {RANGES.map((r) => (
            <button
              key={r.code}
              onClick={() => setRange(r.code)}
              className={`px-2.5 py-1 text-[11px] font-bold rounded-full transition-colors ${
                range === r.code
                  ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black'
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid={`chart-range-${r.code}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-32 sm:h-40">
        {loading || !data || (data.points || []).length < 2 ? (
          <div className="h-full rounded-lg bg-white/[0.03] animate-pulse" />
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={range}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="h-full"
            >
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.points} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradPos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#34d399" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradNeg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f87171" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#f87171" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Tooltip
                    contentStyle={{ background: 'rgba(10,10,10,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '8px 10px' }}
                    labelStyle={{ color: '#a1a1aa', fontSize: 10 }}
                    itemStyle={{ color: '#fafafa', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                    labelFormatter={formatTooltipDate}
                    formatter={(v) => [format(v), 'Value']}
                  />
                  <Area
                    type="monotone"
                    dataKey="v"
                    stroke={stroke}
                    strokeWidth={2}
                    fill={`url(#${fillId})`}
                    isAnimationActive
                    animationDuration={700}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default PortfolioMiniChart;
